import {
    ISettings,
    KeyCodes,
    OnDemandLiveRegion,
    VisuallyHidden,
} from '@ptml/design-system';
import { useNProgress } from '@tanem/react-nprogress';
import { useIsFetching, QueryKey } from '@tanstack/react-query';

import { memo, useEffect } from 'react';
import { useToggle } from 'usehooks-ts';
import { useCountUp } from '../../hooks/useNumberSpring';
import {
    BackgroundContainer,
    Bar,
    Container,
    Counter,
    CurveContainer,
    UnstyledContainer,
} from './ProgressBar.styles';

import { ProgressBarProps } from './ProgressBar.types';
// FIXME: This should be moved to a global type file to be shared across the apps
declare global {
    // Disabled 'cause the right way of doing this is overriding the interface,
    // can't be changed to a type
    // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
    interface DocumentEventMap {
        'progress-start': CustomEvent;
        'progress-stop': CustomEvent;
    }
}

/**
 * Dispatches a custom event 'progress-stop' with the provided key names as the detail.
 *
 * @param {Array<string>} keyName - An array of key names to be included in the event detail.
 */
export const loadingCompleteForKey = (queryKey: QueryKey) =>
    document.dispatchEvent(
        new CustomEvent('progress-stop', { detail: queryKey }),
    );

const TOTAL_PROGRESS = 100;

export const ProgressBar = memo((props: ProgressBarProps) => {
    const {
        color,
        fakeProgress,
        isShort = false,
        queryKey,
        reportCompletion,
        reportKeyProgressOnly,
        showCounter = false,
        useUnstyledContainer = false,
    } = props;

    const isInProgress = useIsFetching(
        queryKey === undefined ? undefined : { queryKey },
    );

    const reportKeyProgress = Boolean(
        reportKeyProgressOnly && queryKey !== undefined,
    );

    let isAnimated = (fakeProgress && fakeProgress > 0) || isInProgress > 0;

    if (queryKey !== undefined) {
        isAnimated = reportKeyProgress && isInProgress > 0;
    }
    const { isFinished, progress, animationDuration } = useNProgress({
        isAnimating: isAnimated,
    });

    const progressValue = (-1 + progress) * TOTAL_PROGRESS;
    const countProgress = fakeProgress ?? progress * TOTAL_PROGRESS;
    const countAnimation = useCountUp(countProgress);

    // Flag used to control whether to announce the progress to the users. Users
    // will toggle those on and off by pressing the spacebar or enter key when
    // the progress bar is progressing AND has focus
    const [announceProgress, toggleAnnounceProgress] = useToggle(false);

    /**
     * This is responsible for announcing the message to the users via live regions
     *
     */
    const announceMessage = (content: string): void => {
        const settings: ISettings = {
            level: 'assertive',
            parentSelector: 'body',
            delay: 0,
        };
        const liveRegionDelayed = new OnDemandLiveRegion(settings);

        liveRegionDelayed.say({
            thingToSay: `${content}`,
        });
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        const { code: keyCode } = event;

        if (keyCode === KeyCodes.ENTER || keyCode === KeyCodes.SPACE) {
            toggleAnnounceProgress();
        }
    };

    useEffect(() => {
        if (announceProgress) {
            if (fakeProgress) {
                announceMessage(`${Math.round(fakeProgress)}%`);
            } else {
                announceMessage(`${Math.round(progress * TOTAL_PROGRESS)}%`);
            }
        }
    }, [fakeProgress, announceProgress, progress]);

    if (isFinished) {
        announceMessage('Loading complete');
    }

    useEffect(() => {
        if (
            reportKeyProgress &&
            reportCompletion &&
            !isInProgress &&
            queryKey
        ) {
            loadingCompleteForKey(queryKey);
        }
    }, [reportKeyProgress, reportCompletion, isInProgress, queryKey]);

    const commonProps = {
        $isShort: isShort,
        $isFinished: isFinished,
        $color: color,
        id: 'progress-bar',
        tabIndex: isInProgress > 0 ? 0 : -1,
        role: 'progressbar',
        'aria-valuemax': 100,
        'aria-valuemin': 0,
        'aria-valuenow': progressValue,
        'aria-describedby': 'progress-bar-title',
        onKeyDown: handleKeyDown,
    };

    const ContainerElement = useUnstyledContainer
        ? UnstyledContainer
        : Container;

    const renderProgressContent = () => (
        <ContainerElement {...commonProps}>
            {/* eslint-disable-next-line i18next/no-literal-string */}
            <VisuallyHidden data-testid="progress-bar-title">
                Press spacebar, or Enter to get progress
            </VisuallyHidden>
            <Bar
                $progress={
                    (fakeProgress && fakeProgress / TOTAL_PROGRESS) ?? progress
                }
                $duration={animationDuration}
                $color={color}
            />
            {showCounter ? (
                <Counter>{`${Math.floor(countAnimation)}%`}</Counter>
            ) : null}
        </ContainerElement>
    );

    return (
        <BackgroundContainer>
            <CurveContainer>{renderProgressContent()}</CurveContainer>
        </BackgroundContainer>
    );
});

ProgressBar.displayName = 'ProgressBar';
export default ProgressBar;
